
import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import * as topojson from "topojson-client";
import HeaderLogo from "../structure/headerlogo";
import CommonIcon from '../common/customicon';


// --- CSS for map and tooltip ---
const mapStyles = `
.info-popup { min-width: 260px; background: white; border-radius: 12px; padding: 16px; box-shadow: 0 8px 32px rgba(0,0,0,0.18); pointer-events: auto; }
.info-popup h3 { font-size: 14px; font-weight: 700; margin: 0 0 6px; }
.info-popup table { font-size: 12px; width: 100%; }
.info-popup td { padding: 2px 4px; vertical-align: top; }
.info-popup .label { color: #888; white-space: nowrap; }
.info-popup .val { font-weight: 600; }
.legend-item { display: flex; align-items: center; gap: 6px; font-size: 12px; }
.pulse { animation: pulse 2s infinite; }
@keyframes pulse { 0%,100%{ opacity:1; transform: scale(1); } 50%{ opacity:0.5; transform: scale(1.3); } }
svg.map-svg { cursor: grab; }
svg.map-svg:active { cursor: grabbing; }
.country { stroke: #cbd5e1; stroke-width: 0.5; transition: fill-opacity 0.2s; }
.country:hover { stroke: #334155; stroke-width: 1.2; fill-opacity: 0.7 !important; }
.country-gap { stroke: #EF4444; stroke-width: 1; stroke-dasharray: 4 2; }
.country-gap:hover { stroke-width: 1.5; fill-opacity: 0.35 !important; }
.overlap-dot { fill: #FBBF24; stroke: #D97706; stroke-width: 2.5; cursor: pointer; filter: drop-shadow(0 2px 4px rgba(217,119,6,0.4)); }
.tooltip-container { position: absolute; pointer-events: none; z-index: 50; transition: opacity 0.15s; }
.sidebar-card:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
.stripe-blue { fill: url(#stripes-blue); }
.stripe-purple { fill: url(#stripes-purple); }
`;

const TerritoryMapping = () => {
  const mapRef = useRef(null);
  const tooltipRef = useRef(null);
  const [brand, setBrand] = useState("all");

  // --- Insert CSS for map/tooltip styles ---
  useEffect(() => {
    if (!document.getElementById("territory-map-styles")) {
      const style = document.createElement("style");
      style.id = "territory-map-styles";
      style.innerHTML = mapStyles;
      document.head.appendChild(style);
    }
  }, []);

  // --- D3/TopoJSON map logic ---
  useEffect(() => {
    // --- Data and config (from HTML mockup, trimmed for brevity) ---
    const isoAlpha3ToNumeric = { USA: "840", CAN: "124", GBR: "826", FRA: "250", DEU: "276", ITA: "380", ESP: "724", NLD: "528", BEL: "056", PRT: "620", AUT: "040", CHE: "756", SWE: "752", NOR: "578", DNK: "208", FIN: "246", IRL: "372", POL: "616", CZE: "203", GRC: "300", HUN: "348", ROU: "642", BGR: "100", HRV: "191", SVK: "703", SVN: "705", JPN: "392", KOR: "410", AUS: "036", NZL: "554", BRA: "076", MEX: "484", ARG: "032", COL: "170", CHL: "152", PER: "604", VEN: "862", ECU: "218", BOL: "068", PRY: "600", URY: "858", GUY: "328", SUR: "740", PAN: "591", CRI: "188", GTM: "320", HND: "340", SLV: "222", NIC: "558", CUB: "192", DOM: "214", HTI: "332", JAM: "388", TTO: "780", SAU: "682", ARE: "784", QAT: "634", KWT: "414", BHR: "048", OMN: "512", IRQ: "368", IRN: "364", EGY: "818", LBY: "434", TUN: "788", DZA: "012", MAR: "504", JOR: "400", LBN: "422", SYR: "760", YEM: "887", ISR: "376", PSE: "275", ZAF: "710", NGA: "566", KEN: "404", GHA: "288", ETH: "231", TZA: "834", UGA: "800", RWA: "646", SEN: "686", CIV: "384", CMR: "120", AGO: "024", MOZ: "508", ZMB: "894", ZWE: "716", BWA: "072", NAM: "516", GAB: "266", COG: "178", COD: "180", MLI: "466", BFA: "854", NER: "562", TCD: "148", SDN: "729", SSD: "728", SOM: "706", MWI: "454", MDG: "450", IND: "356", PAK: "586", BGD: "050", LKA: "144", NPL: "524", MMR: "104", BTN: "064", RUS: "643", UKR: "804", KAZ: "398", UZB: "860", BLR: "112", AZE: "031", GEO: "268", ARM: "051", TKM: "795", KGZ: "417", TJK: "762", MDA: "498" };
    const numericToAlpha3 = {};
    Object.entries(isoAlpha3ToNumeric).forEach(([a3, num]) => { numericToAlpha3[num] = a3; numericToAlpha3[String(parseInt(num))] = a3; });
    const territoryConfig = {
      aurora: [
        { name: "North America (US + Canada)", countries: ["USA", "CAN"], color: "#3B82F6", licensee: "Summit Ridge Merch, Golden State Apparel", revenue: "$6.7M", categories: "Apparel, Toys, Stationery, Accessories", exclusive: "Mixed" },
        { name: "Europe + UK", countries: ["GBR", "FRA", "DEU", "ITA", "ESP", "NLD", "BEL", "PRT", "AUT", "CHE", "SWE", "NOR", "DNK", "FIN", "IRL", "POL", "CZE", "GRC", "HUN", "ROU", "BGR", "HRV", "SVK", "SVN"], color: "#3B82F6", licensee: "Bright Star Retail", revenue: "$3.8M", categories: "Apparel, Home Decor", exclusive: "Non-exclusive" },
        { name: "Japan + South Korea", countries: ["JPN", "KOR"], color: "#3B82F6", licensee: "Asia Licensing Corp", revenue: "$1.5M", categories: "Apparel, Toys, Digital", exclusive: "Exclusive" }
      ],
      midnight: [
        { name: "North America", countries: ["USA", "CAN"], color: "#8B5CF6", licensee: "Horizon Peak Retail, NovaTech Games", revenue: "$1.3M", categories: "Retail Merch, Games", exclusive: "Non-exclusive" },
        { name: "Australia & New Zealand", countries: ["AUS", "NZL"], color: "#8B5CF6", licensee: "SoundWave Merch", revenue: "$1.05M", categories: "Apparel, Accessories", exclusive: "Exclusive" },
        { name: "UK & Ireland", countries: ["GBR", "IRL"], color: "#8B5CF6", licensee: "Echo Music Merch Ltd", revenue: "$750K", categories: "Apparel, Music Accessories", exclusive: "Exclusive" }
      ],
      starblaze: [
        { name: "United States", countries: ["USA"], color: "#F97316", licensee: "Pacific Coast Toys, Action Planet Inc", revenue: "$480K", categories: "Toys, Action Figures", exclusive: "Non-exclusive" },
        { name: "Global (Dreamlight Studios)", countries: ["GBR", "FRA", "DEU", "ITA", "ESP", "JPN", "KOR", "AUS", "NZL", "BRA", "MEX", "ARG", "CHN", "IND", "ZAF", "NGA", "EGY", "SAU", "ARE", "TUR", "IDN", "THA", "VNM", "MYS", "PHL", "SGP", "TWN", "CAN"], color: "F97316", licensee: "Dreamlight Studios", revenue: "$2.1M", categories: "Toys, Games, Digital", exclusive: "Exclusive" }
      ]
    };
    const gapCountries = {
      "Latin America": ["BRA", "MEX", "ARG", "COL", "CHL", "PER", "VEN", "ECU", "BOL", "PRY", "URY", "GUY", "SUR", "PAN", "CRI", "GTM", "HND", "SLV", "NIC", "CUB", "DOM", "HTI", "JAM", "TTO"],
      "Middle East & North Africa": ["SAU", "ARE", "QAT", "KWT", "BHR", "OMN", "IRQ", "IRN", "EGY", "LBY", "TUN", "DZA", "MAR", "JOR", "LBN", "SYR", "YEM", "ISR", "PSE"],
      "Sub-Saharan Africa": ["ZAF", "NGA", "KEN", "GHA", "ETH", "TZA", "UGA", "RWA", "SEN", "CIV", "CMR", "AGO", "MOZ", "ZMB", "ZWE", "BWA", "NAM", "GAB", "COG", "COD", "MLI", "BFA", "NER", "TCD", "SDN", "SSD", "SOM", "MWI", "MDG"],
      "South Asia": ["IND", "PAK", "BGD", "LKA", "NPL", "MMR", "BTN"],
      "Russia & CIS": ["RUS", "UKR", "KAZ", "UZB", "BLR", "AZE", "GEO", "ARM", "TKM", "KGZ", "TJK", "MDA"]
    };
    const overlapPoints = [
      { lat: 36.7, lng: -119.4, title: "US West Coast -- Apparel", detail: "Summit Ridge Merch vs Golden State Apparel<br>Both hold apparel rights in California region", brands: ["Aurora Animation", "Midnight Echo", "StarBlaze Heroes"] },
      { lat: 44.0, lng: -79.0, title: "Ontario, Canada -- Toys", detail: "Pacific Coast Toys vs Northern Lights Retail<br>Overlapping toy distribution rights", brands: ["Aurora Animation", "Midnight Echo"] }
    ];
    const countryNames = { "840": "United States", "124": "Canada", "826": "United Kingdom", "250": "France", "276": "Germany", "380": "Italy", "724": "Spain", "528": "Netherlands", "056": "Belgium", "620": "Portugal", "040": "Austria", "756": "Switzerland", "752": "Sweden", "578": "Norway", "208": "Denmark", "246": "Finland", "372": "Ireland", "616": "Poland", "203": "Czech Republic", "300": "Greece", "348": "Hungary", "642": "Romania", "100": "Bulgaria", "191": "Croatia", "703": "Slovakia", "705": "Slovenia", "392": "Japan", "410": "South Korea", "036": "Australia", "554": "New Zealand", "076": "Brazil", "484": "Mexico", "032": "Argentina", "170": "Colombia", "152": "Chile", "604": "Peru", "862": "Venezuela", "218": "Ecuador", "068": "Bolivia", "600": "Paraguay", "858": "Uruguay", "328": "Guyana", "740": "Suriname", "591": "Panama", "188": "Costa Rica", "320": "Guatemala", "340": "Honduras", "222": "El Salvador", "558": "Nicaragua", "192": "Cuba", "214": "Dominican Republic", "332": "Haiti", "388": "Jamaica", "780": "Trinidad and Tobago", "682": "Saudi Arabia", "784": "UAE", "634": "Qatar", "414": "Kuwait", "048": "Bahrain", "512": "Oman", "368": "Iraq", "364": "Iran", "818": "Egypt", "434": "Libya", "788": "Tunisia", "012": "Algeria", "504": "Morocco", "400": "Jordan", "422": "Lebanon", "760": "Syria", "887": "Yemen", "376": "Israel", "275": "Palestine", "710": "South Africa", "566": "Nigeria", "404": "Kenya", "288": "Ghana", "231": "Ethiopia", "834": "Tanzania", "800": "Uganda", "646": "Rwanda", "686": "Senegal", "384": "Ivory Coast", "120": "Cameroon", "024": "Angola", "508": "Mozambique", "894": "Zambia", "716": "Zimbabwe", "072": "Botswana", "516": "Namibia", "266": "Gabon", "178": "Congo", "180": "DR Congo", "466": "Mali", "854": "Burkina Faso", "562": "Niger", "148": "Chad", "729": "Sudan", "728": "South Sudan", "706": "Somalia", "454": "Malawi", "450": "Madagascar", "356": "India", "586": "Pakistan", "050": "Bangladesh", "144": "Sri Lanka", "524": "Nepal", "104": "Myanmar", "064": "Bhutan", "643": "Russia", "804": "Ukraine", "398": "Kazakhstan", "860": "Uzbekistan", "112": "Belarus", "031": "Azerbaijan", "268": "Georgia", "051": "Armenia", "795": "Turkmenistan", "417": "Kyrgyzstan", "762": "Tajikistan", "498": "Moldova" };

    function buildCountryBrandMap(activeBrands) {
      const map = {};
      activeBrands.forEach(b => {
        const brandLabel = b === "aurora" ? "Aurora Animation" : b === "midnight" ? "Midnight Echo" : "StarBlaze Heroes";
        (territoryConfig[b] || []).forEach(t => {
          t.countries.forEach(iso => {
            if (!map[iso]) map[iso] = [];
            map[iso].push({ brand: b, brandLabel, territory: t });
          });
        });
      });
      return map;
    }
    function buildGapSet(activeBrands, coveredSet) {
      const gapSet = {};
      Object.entries(gapCountries).forEach(([region, countries]) => {
        countries.forEach(iso => {
          if (!coveredSet.has(iso)) {
            gapSet[iso] = region;
          }
        });
      });
      return gapSet;
    }
    function getCountryFill(iso, countryBrandMap, gapSet) {
      const brands = countryBrandMap[iso];
      if (!brands || brands.length === 0) {
        if (gapSet[iso]) return "url(#gap-fill)";
        return "#e5e7eb";
      }
      if (brands.length === 1) {
        return brands[0].territory.color;
      }
      const brandKeys = [...new Set(brands.map(b => b.brand))];
      if (brandKeys.length >= 3) return "url(#stripe-triple)";
      if (brandKeys.includes("aurora") && brandKeys.includes("midnight")) return "url(#stripe-blue-purple)";
      if (brandKeys.includes("aurora") && brandKeys.includes("starblaze")) return "url(#stripe-blue-orange)";
      if (brandKeys.includes("midnight") && brandKeys.includes("starblaze")) return "url(#stripe-purple-orange)";
      return brands[0].territory.color;
    }
    function getCountryFillOpacity(iso, countryBrandMap, gapSet) {
      const brands = countryBrandMap[iso];
      if (!brands || brands.length === 0) {
        if (gapSet[iso]) return 1;
        return 1;
      }
      if (brands.length > 1) return 1;
      return 0.4;
    }
    function getCountryStroke(iso, gapSet) {
      if (gapSet[iso]) return "#EF4444";
      return "#cbd5e1";
    }
    function getCountryStrokeDash(iso, gapSet) {
      if (gapSet[iso]) return "4,2";
      return "none";
    }
    function getCountryStrokeWidth(iso, gapSet) {
      if (gapSet[iso]) return 1;
      return 0.5;
    }
    function buildTooltipHtml(iso, countryBrandMap, gapSet, countryName) {
      const brands = countryBrandMap[iso];
      if (brands && brands.length > 0) {
        let html = `<h3>${countryName}</h3>`;
        const seen = new Set();
        brands.forEach(b => {
          const key = b.brandLabel + '|' + b.territory.name;
          if (seen.has(key)) return;
          seen.add(key);
          html += `\n<div style="display:inline-block;background:${b.territory.color}20;color:${b.territory.color};padding:2px 8px;border-radius:10px;font-size:11px;font-weight:600;margin:4px 4px 4px 0">${b.brandLabel}</div>`;
        });
        const primary = brands[0].territory;
        html += `<table style="margin-top:8px"><tr><td class="label">Territory:</td><td class="val">${primary.name}</td></tr><tr><td class="label">Licensee:</td><td class="val">${primary.licensee}</td></tr><tr><td class="label">Revenue:</td><td class="val" style="color:#16a34a">${primary.revenue}</td></tr><tr><td class="label">Categories:</td><td>${primary.categories}</td></tr><tr><td class="label">Exclusivity:</td><td>${primary.exclusive}</td></tr></table>`;
        if (brands.length > 1) {
          html += `<div style="margin-top:8px;padding-top:8px;border-top:1px solid #e5e7eb;font-size:11px;color:#92400E;font-weight:600">+ ${brands.length - 1} more brand(s) licensed here</div>`;
        }
        return html;
      }
      if (gapSet[iso]) {
        const region = gapSet[iso];
        const marketEst = {
          "Latin America": "$2.1M/yr",
          "Middle East & North Africa": "$800K/yr",
          "Sub-Saharan Africa": "$400K/yr",
          "South Asia": "$1.8M/yr",
          "Russia & CIS": "$600K/yr"
        }[region] || '';
        return `<h3 style="color:#DC2626">${countryName}</h3>\n<div style="display:inline-block;background:#FEE2E2;color:#DC2626;padding:2px 8px;border-radius:10px;font-size:11px;font-weight:600;margin:4px 0">GAP - ${region}</div>\n<p style="font-size:12px;margin-top:6px">No active license in this territory</p>\n<p style="font-size:12px;color:#16a34a;font-weight:600">Est. market: ${marketEst}</p>`;
      }
      return `<h3>${countryName}</h3><p style="font-size:12px;color:#9ca3af">No licensing data</p>`;
    }
    function getCountryName(feature) {
      const id = String(feature.id);
      return countryNames[id] || feature.properties?.name || `Country ${id}`;
    }
    function getAlpha3(feature) {
      const id = String(feature.id);
      return numericToAlpha3[id] || null;
    }

    // --- Map rendering ---
    const container = mapRef.current;
    if (!container) return;
    container.innerHTML = "";
    const width = container.offsetWidth || 900;
    const height = 580;
    const svg = d3.select(container)
      .append("svg")
      .attr("class", "map-svg")
      .attr("width", "100%")
      .attr("height", height)
      .attr("viewBox", `0 0 ${width} ${height}`);
    const defs = svg.append("defs");
    function addStripePattern(id, color1, color2) {
      const pat = defs.append("pattern")
        .attr("id", id)
        .attr("patternUnits", "userSpaceOnUse")
        .attr("width", 8)
        .attr("height", 8)
        .attr("patternTransform", "rotate(45)");
      pat.append("rect").attr("width", 8).attr("height", 8).attr("fill", color1).attr("fill-opacity", 0.4);
      pat.append("line").attr("x1", 0).attr("y1", 0).attr("x2", 0).attr("y2", 8)
        .attr("stroke", color2).attr("stroke-width", 4).attr("stroke-opacity", 0.5);
    }
    addStripePattern("stripe-blue-purple", "#3B82F6", "#8B5CF6");
    addStripePattern("stripe-blue-orange", "#3B82F6", "#F97316");
    addStripePattern("stripe-purple-orange", "#8B5CF6", "#F97316");
    addStripePattern("stripe-all-three", "#3B82F6", "#8B5CF6");
    const triPat = defs.append("pattern")
      .attr("id", "stripe-triple")
      .attr("patternUnits", "userSpaceOnUse")
      .attr("width", 12)
      .attr("height", 12)
      .attr("patternTransform", "rotate(45)");
    triPat.append("rect").attr("width", 12).attr("height", 12).attr("fill", "#3B82F6").attr("fill-opacity", 0.35);
    triPat.append("line").attr("x1", 0).attr("y1", 0).attr("x2", 0).attr("y2", 12)
      .attr("stroke", "#8B5CF6").attr("stroke-width", 4).attr("stroke-opacity", 0.5);
    triPat.append("line").attr("x1", 8).attr("y1", 0).attr("x2", 8).attr("y2", 12)
      .attr("stroke", "#F97316").attr("stroke-width", 3).attr("stroke-opacity", 0.5);
    const gapPat = defs.append("pattern")
      .attr("id", "gap-fill")
      .attr("patternUnits", "userSpaceOnUse")
      .attr("width", 6)
      .attr("height", 6);
    gapPat.append("rect").attr("width", 6).attr("height", 6).attr("fill", "#FCA5A5").attr("fill-opacity", 0.2);
    gapPat.append("line").attr("x1", 0).attr("y1", 6).attr("x2", 6).attr("y2", 0)
      .attr("stroke", "#EF4444").attr("stroke-width", 0.5).attr("stroke-opacity", 0.3);
    const projection = d3.geoNaturalEarth1()
      .scale(width / 5.5)
      .translate([width / 2, height / 2]);
    const path = d3.geoPath().projection(projection);
    const g = svg.append("g");
    const zoom = d3.zoom()
      .scaleExtent([1, 12])
      .on("zoom", (event) => {
        g.attr("transform", event.transform);
      });
    svg.call(zoom);
    // Tooltip logic
    const tooltip = tooltipRef.current;
    function showTooltip(event, html) {
      if (!tooltip) return;
      tooltip.innerHTML = html;
      tooltip.style.opacity = 1;
      const rect = container.getBoundingClientRect();
      let x = event.clientX - rect.left + 15;
      let y = event.clientY - rect.top - 10;
      if (x + 280 > rect.width) x = x - 290;
      if (y + 200 > rect.height) y = y - 150;
      if (y < 0) y = 10;
      tooltip.style.left = x + "px";
      tooltip.style.top = y + "px";
    }
    function hideTooltip() {
      if (tooltip) tooltip.style.opacity = 0;
    }
    // --- Load TopoJSON ---
    d3.json("https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json").then((topoData) => {
      const countries = topojson.feature(topoData, topoData.objects.countries);
      // --- Brand filter logic ---
      const activeBrands = brand === "all" ? ["aurora", "midnight", "starblaze"] : [brand];
      const countryBrandMap = buildCountryBrandMap(activeBrands);
      const coveredSet = new Set(Object.keys(countryBrandMap));
      const showGaps = brand === "all" || brand === "aurora";
      const gapSet = showGaps ? buildGapSet(activeBrands, coveredSet) : {};
      // Draw globe background
      g.append("path")
        .datum({ type: "Sphere" })
        .attr("d", path)
        .attr("fill", "#f0f9ff")
        .attr("stroke", "#bfdbfe")
        .attr("stroke-width", 0.5);
      // Draw countries
      g.selectAll(".country-path")
        .data(countries.features)
        .enter()
        .append("path")
        .attr("d", path)
        .attr("class", d => {
          const iso = getAlpha3(d);
          if (iso && gapSet[iso]) return "country country-gap";
          return "country";
        })
        .attr("fill", d => {
          const iso = getAlpha3(d);
          if (!iso) return "#e5e7eb";
          return getCountryFill(iso, countryBrandMap, gapSet);
        })
        .attr("fill-opacity", d => {
          const iso = getAlpha3(d);
          if (!iso) return 1;
          return getCountryFillOpacity(iso, countryBrandMap, gapSet);
        })
        .attr("stroke", d => {
          const iso = getAlpha3(d);
          if (!iso) return "#cbd5e1";
          return getCountryStroke(iso, gapSet);
        })
        .attr("stroke-dasharray", d => {
          const iso = getAlpha3(d);
          if (!iso) return "none";
          return getCountryStrokeDash(iso, gapSet);
        })
        .attr("stroke-width", d => {
          const iso = getAlpha3(d);
          if (!iso) return 0.5;
          return getCountryStrokeWidth(iso, gapSet);
        })
        .on("mouseover", function (event, d) {
          const iso = getAlpha3(d);
          const name = getCountryName(d);
          d3.select(this).raise();
          if (iso) {
            showTooltip(event, buildTooltipHtml(iso, countryBrandMap, gapSet, name));
          } else {
            showTooltip(event, `<h3>${name}</h3><p style=\"font-size:12px;color:#9ca3af\">No licensing data</p>`);
          }
        })
        .on("mousemove", function (event) {
          const rect = container.getBoundingClientRect();
          let x = event.clientX - rect.left + 15;
          let y = event.clientY - rect.top - 10;
          if (x + 280 > rect.width) x = x - 290;
          if (y + 200 > rect.height) y = y - 150;
          if (y < 0) y = 10;
          tooltip.style.left = x + "px";
          tooltip.style.top = y + "px";
        })
        .on("mouseout", function () {
          hideTooltip();
        });
      // Draw country borders
      g.append("path")
        .datum(topojson.mesh(topoData, topoData.objects.countries, (a, b) => a !== b))
        .attr("d", path)
        .attr("fill", "none")
        .attr("stroke", "#94a3b8")
        .attr("stroke-width", 0.3)
        .attr("stroke-opacity", 0.6)
        .style("pointer-events", "none");
      // Draw overlap markers
      overlapPoints.forEach(o => {
        const [cx, cy] = projection([o.lng, o.lat]);
        if (cx === undefined || cy === undefined) return;
        g.append("circle")
          .attr("cx", cx)
          .attr("cy", cy)
          .attr("r", 12)
          .attr("fill", "#FBBF24")
          .attr("fill-opacity", 0.2)
          .attr("class", "pulse")
          .style("pointer-events", "none");
        g.append("circle")
          .attr("cx", cx)
          .attr("cy", cy)
          .attr("r", 7)
          .attr("class", "overlap-dot pulse")
          .on("mouseover", function (event) {
            showTooltip(event, `\n<h3 style=\"color:#92400E\">&#9888; ${o.title}</h3>\n<p style=\"font-size:12px;margin:6px 0\">${o.detail}</p>\n<div style=\"font-size:11px;color:#6b7280\">Brands: ${o.brands.join(", ")}</div>\n`);
          })
          .on("mouseout", hideTooltip);
        g.append("text")
          .attr("x", cx)
          .attr("y", cy + 1)
          .attr("text-anchor", "middle")
          .attr("dominant-baseline", "central")
          .attr("font-size", 10)
          .attr("font-weight", "bold")
          .attr("fill", "#92400E")
          .style("pointer-events", "none")
          .text("!");
      });
    });
    // Clean up on unmount
    return () => { container.innerHTML = ""; };
  }, [brand]);

  // --- Brand filter handler ---
  const handleBrandChange = (e) => setBrand(e.target.value);

  // --- Tooltip absolute div ---
  // (Positioned absolutely inside map container)

  return (
    <div className="main-content" style={{ paddingTop: 64 }}>
      <HeaderLogo />
      <div className="page-content">
        <div className="container-fluid">
          <div id="table_height">
            <div className="">
              <div className="flex items-center justify-between mb-6">
                <div className="d-flex align-items-center mb-2">
              <div className="bg-primary text-white rounded p-2 me-3">
                <CommonIcon iconName="Map" size={"24px"}  color="#fff" />
              </div>
              <div className="flex-grow-1">
                <h1 className="h3 mb-0 fw-bold">Territory Mapping</h1>
                <p className="text-muted mb-0 small">Visualize licensed territories, identify overlaps and gaps</p>
              </div>
</div>
                {/* <div>
                  <h1 className="text-3xl font-bold text-gray-900">Territory Mapping</h1>
                  <p className="text-gray-500 mt-1">Visualize licensed territories, identify overlaps and gaps</p>
                </div> */}
                <div className="flex gap-2">
                  <select value={brand} onChange={handleBrandChange} className="border rounded-lg px-4 py-2 bg-white text-sm font-medium focus:ring-2 focus:ring-blue-300 focus:outline-none">
                    <option value="all">All Brands</option>
                    <option value="aurora">Aurora Animation</option>
                    <option value="midnight">Midnight Echo</option>
                    <option value="starblaze">StarBlaze Heroes</option>
                  </select>
                </div>
              </div>
              {/* Alert */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6 flex items-center gap-3">
                <span className="text-2xl">&#9888;</span>
                <div>
                  <p className="font-semibold text-yellow-800">2 Territory Overlaps Detected</p>
                  <p className="text-sm text-yellow-600">Overlapping rights in US West Coast (Apparel) and Ontario Canada (Toys)</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-6">
                {/* Map */}
                <div className="col-span-2 bg-white rounded-xl shadow overflow-hidden" style={{ position: "relative" }}>
                  <div ref={mapRef} id="map-container" style={{ height: 580, position: "relative", overflow: "hidden" }} />
                  {/* Tooltip absolute div */}
                  <div ref={tooltipRef} className="tooltip-container info-popup" style={{ opacity: 0, position: "absolute", pointerEvents: "none", zIndex: 50 }} />
                  <div className="flex gap-5 p-4 border-t bg-gray-50 flex-wrap">
                    <div className="legend-item"><div className="w-5 h-3 rounded" style={{ background: "#3B82F6", opacity: 0.5 }}></div>Aurora Animation</div>
                    <div className="legend-item"><div className="w-5 h-3 rounded" style={{ background: "#8B5CF6", opacity: 0.5 }}></div>Midnight Echo</div>
                    <div className="legend-item"><div className="w-5 h-3 rounded" style={{ background: "#F97316", opacity: 0.5 }}></div>StarBlaze Heroes</div>
                    <div className="legend-item"><div className="w-5 h-3 rounded" style={{ background: "#FCA5A5", border: "2px dashed #EF4444" }}></div>Unlicensed Gap</div>
                    <div className="legend-item"><div className="w-4 h-4 rounded-full" style={{ background: "#FBBF24", border: "2px solid #D97706" }}></div>Overlap</div>
                    <div className="legend-item"><div className="w-5 h-3 rounded" style={{ background: "repeating-linear-gradient(45deg,#3B82F6,#3B82F6 2px,#8B5CF6 2px,#8B5CF6 4px)", opacity: 0.6 }}></div>Multi-brand</div>
                  </div>
                </div>
                {/* Details Panel */}
                <div className="space-y-4 overflow-y-auto" style={{ maxHeight: 660 }}>
                  <div className="bg-white rounded-xl shadow p-5 sidebar-card">
                    <h5 className="font-semibold text-lg mb-3">Coverage Summary</h5>
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between text-sm mb-1"><span>Global Coverage</span><span className="font-semibold">62%</span></div>
                        <div className="w-full bg-gray-200 rounded-full h-3"><div className="bg-blue-600 h-3 rounded-full" style={{ width: "62%" }}></div></div>
                      </div>
                      <div>
                        <div className="flex justify-between text-sm mb-1"><span>Total Licensed Revenue</span><span className="font-semibold">$11.5M</span></div>
                        <div className="w-full bg-gray-200 rounded-full h-3"><div className="bg-green-600 h-3 rounded-full" style={{ width: "85%" }}></div></div>
                      </div>
                      <div>
                        <div className="flex justify-between text-sm mb-1"><span>Active Licensees</span><span className="font-semibold">13</span></div>
                        <div className="w-full bg-gray-200 rounded-full h-3"><div className="bg-purple-600 h-3 rounded-full" style={{ width: "72%" }}></div></div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow p-5 sidebar-card">
                    <h5 className="font-semibold text-lg mb-3 text-yellow-700">Overlaps (2)</h5>
                    <div className="space-y-3">
                      <div className="border-l-4 border-yellow-500 pl-3 py-1 cursor-pointer hover:bg-yellow-50 rounded-r">
                        <p className="font-medium text-sm">US West Coast -- Apparel</p>
                        <p className="text-xs text-gray-500">Summit Ridge <span className="text-red-500 font-bold">vs</span> Golden State</p>
                        <p className="text-xs text-yellow-600 mt-1">Both have apparel rights in California</p>
                      </div>
                      <div className="border-l-4 border-yellow-500 pl-3 py-1 cursor-pointer hover:bg-yellow-50 rounded-r">
                        <p className="font-medium text-sm">Canada -- Toys</p>
                        <p className="text-xs text-gray-500">Pacific Coast <span className="text-red-500 font-bold">vs</span> Northern Lights</p>
                        <p className="text-xs text-yellow-600 mt-1">Overlapping toy distribution in Ontario</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow p-5 sidebar-card">
                    <h3 className="font-semibold text-lg mb-3 text-red-700">Gaps (5 Regions)</h3>
                    <div className="space-y-3">
                      <div className="border-l-4 border-red-500 pl-3 py-1 cursor-pointer hover:bg-red-50 rounded-r">
                        <p className="font-medium text-sm">Latin America</p>
                        <p className="text-xs text-gray-500">No licensee for any category</p>
                        <p className="text-xs text-green-600 mt-1">Est. market: $2.1M/year</p>
                      </div>
                      <div className="border-l-4 border-red-500 pl-3 py-1 cursor-pointer hover:bg-red-50 rounded-r">
                        <p className="font-medium text-sm">Middle East & North Africa</p>
                        <p className="text-xs text-gray-500">No licensee for any category</p>
                        <p className="text-xs text-green-600 mt-1">Est. market: $800K/year</p>
                      </div>
                      <div className="border-l-4 border-red-500 pl-3 py-1 cursor-pointer hover:bg-red-50 rounded-r">
                        <p className="font-medium text-sm">Sub-Saharan Africa</p>
                        <p className="text-xs text-gray-500">No licensee for any category</p>
                        <p className="text-xs text-green-600 mt-1">Est. market: $400K/year</p>
                      </div>
                      <div className="border-l-4 border-red-500 pl-3 py-1 cursor-pointer hover:bg-red-50 rounded-r">
                        <p className="font-medium text-sm">South Asia (India)</p>
                        <p className="text-xs text-gray-500">No licensee for any category</p>
                        <p className="text-xs text-green-600 mt-1">Est. market: $1.8M/year</p>
                      </div>
                      <div className="border-l-4 border-red-500 pl-3 py-1 cursor-pointer hover:bg-red-50 rounded-r">
                        <p className="font-medium text-sm">Russia & CIS</p>
                        <p className="text-xs text-gray-500">No licensee for any category</p>
                        <p className="text-xs text-green-600 mt-1">Est. market: $600K/year</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TerritoryMapping;
